import React, { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Chip,
  Link,
  MenuItem,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import HotelIcon from '@mui/icons-material/Hotel';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import moment from 'moment';
import { DataTable, FormDatePicker, FormSelect, FormTextField } from '../../shared';
import DashboardReportHeader from '../common/DashboardReportHeader';
import { getWebCheckInInvites } from '../../services/api';
import { formatHotelDate, formatHotelDateTime } from '../../utils/dateUtils';
import WebCheckInInviteDrawer from './WebCheckInInviteDrawer';

const DEFAULT_SORT = { column: 'created_at', direction: 'desc' };
const ACCENT = '#4ce1b6';

const STATUS_STYLES = {
  pending: { bg: 'rgba(255, 152, 0, 0.14)', color: '#e65100' },
  submitted: { bg: 'rgba(76, 175, 80, 0.14)', color: '#2e7d32' },
  expired: { bg: 'rgba(158, 158, 158, 0.16)', color: '#616161' },
  cancelled: { bg: 'rgba(244, 67, 54, 0.14)', color: '#c62828' },
};

const createDefaultFilters = () => ({
  guest: '',
  phone: '',
  room: '',
  status: '',
  payment_status: '',
  check_in_date: '',
  check_in_from: '',
  check_in_to: '',
});

function buildParams(filters, pagination, sortConfig) {
  const sortColumn = sortConfig.column || 'created_at';
  const sortDirection = sortConfig.direction || 'desc';
  const params = {
    page: pagination.page + 1,
    per_page: pagination.perPage,
    sort: sortDirection === 'desc' ? `-${sortColumn}` : sortColumn,
  };

  ['guest', 'phone', 'room', 'status', 'payment_status'].forEach((key) => {
    if (filters[key]) params[key] = filters[key];
  });

  if (filters.check_in_date) {
    params.check_in_from = filters.check_in_date;
    params.check_in_to = filters.check_in_date;
  } else {
    if (filters.check_in_from) params.check_in_from = filters.check_in_from;
    if (filters.check_in_to) params.check_in_to = filters.check_in_to;
  }

  return params;
}

function StatusChip({ status, label }) {
  const key = String(status || '').toLowerCase();
  const style = STATUS_STYLES[key] || STATUS_STYLES.expired;
  return (
    <Chip
      size="small"
      label={label || status || '—'}
      sx={{
        height: 24,
        fontWeight: 700,
        fontSize: '0.72rem',
        backgroundColor: style.bg,
        color: style.color,
      }}
    />
  );
}

function formatStayDate(value) {
  if (!value || value === '—') return '—';
  const parsed = moment(value);
  return parsed.isValid() ? formatHotelDate(value) : '—';
}

function formatDateTime(value) {
  if (!value) return '—';
  return formatHotelDateTime(value) || '—';
}

function StayLine({ Icon, iconColor, label, date }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ lineHeight: 1.2 }}>
      <Icon sx={{ fontSize: 14, color: iconColor, flexShrink: 0, opacity: 0.92 }} />
      <Typography
        component="span"
        variant="caption"
        sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.65rem', minWidth: 22 }}
      >
        {label}
      </Typography>
      <Typography component="span" variant="body2" sx={{ color: 'text.primary', fontWeight: 600, fontSize: '0.78rem' }}>
        {date}
      </Typography>
    </Stack>
  );
}

function InviteStayCell({ row }) {
  const nights = Number(row?.nights || 0);
  const hasNights = Number.isFinite(nights) && nights > 0;
  return (
    <Stack spacing={0.25} sx={{ py: 0.15, minWidth: 155 }}>
      <StayLine
        Icon={LoginIcon}
        iconColor={ACCENT}
        label="In"
        date={formatStayDate(row?.check_in_date)}
      />
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <StayLine
          Icon={LogoutIcon}
          iconColor="#60a5fa"
          label="Out"
          date={formatStayDate(row?.check_out_date)}
        />
        {hasNights ? (
          <Typography
            variant="caption"
            sx={{
              ml: 0.25,
              color: ACCENT,
              fontWeight: 800,
              fontSize: '0.62rem',
              whiteSpace: 'nowrap',
            }}
          >
            · {nights}N
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  );
}

function RoomCell({ row }) {
  const number = row?.room?.number || '—';
  const typeName = row?.room?.room_type_name || '';
  const imageUrl = row?.room?.image_url || '';
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, py: 0.15 }}>
      <Avatar
        src={imageUrl || undefined}
        variant="rounded"
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: 1.5,
          bgcolor: 'rgba(76, 225, 182, 0.12)',
          color: ACCENT,
          border: '1px solid rgba(76, 225, 182, 0.22)',
        }}
      >
        <HotelIcon sx={{ fontSize: 20 }} />
      </Avatar>
      <Stack spacing={0.1} sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={700} noWrap>
          {number}
        </Typography>
        {typeName ? (
          <Typography variant="caption" color="text.secondary" noWrap>
            {typeName}
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  );
}

function ContactCell({ row }) {
  const email = String(row?.guest_email || '').trim();
  const phone = String(row?.guest_phone || '').trim();
  if (!email && !phone) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Stack spacing={0.15} sx={{ minWidth: 0, py: 0.15 }}>
      {phone ? (
        <Typography variant="body2" fontWeight={600} noWrap title={phone}>
          {phone}
        </Typography>
      ) : null}
      {email ? (
        <Typography variant="caption" color="text.secondary" noWrap title={email}>
          {email}
        </Typography>
      ) : null}
    </Stack>
  );
}

function LinkTimingCell({ row }) {
  const expiresAt = row?.expires_at;
  const sentAt = row?.email_sent_at || row?.created_at;
  const expired = Boolean(row?.is_expired) || String(row?.status || '').toLowerCase() === 'expired';
  return (
    <Stack spacing={0.3} sx={{ py: 0.15, minWidth: 150 }}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <ScheduleIcon
          sx={{
            fontSize: 14,
            color: expired ? '#ef5350' : ACCENT,
            flexShrink: 0,
          }}
        />
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            fontSize: '0.78rem',
            color: expired ? '#ef5350' : 'text.primary',
            whiteSpace: 'nowrap',
          }}
        >
          {formatDateTime(expiresAt)}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <SendOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary', flexShrink: 0, opacity: 0.85 }} />
        <Typography variant="caption" color="text.secondary" noWrap>
          Sent {formatDateTime(sentAt)}
        </Typography>
      </Stack>
    </Stack>
  );
}

const WebCheckInList = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [toolbarResetKey, setToolbarResetKey] = useState(0);
  const [filters, setFilters] = useState(() => createDefaultFilters());
  const [sortConfig, setSortConfig] = useState({ ...DEFAULT_SORT });
  const [pagination, setPagination] = useState({
    page: 0,
    perPage: 10,
    totalRecords: 0,
  });
  const [selectedInviteId, setSelectedInviteId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchInvites = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getWebCheckInInvites(buildParams(filters, pagination, sortConfig));
      setRows(response?.Data || []);
      setPagination((prev) => ({
        ...prev,
        totalRecords: response?.total_records || 0,
      }));
    } catch (err) {
      setError(err.message || 'Failed to fetch web check-in invites');
    } finally {
      setLoading(false);
    }
  }, [filters, sortConfig, pagination.page, pagination.perPage]);

  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleDateRangeChange = (nextStart, nextEnd) => {
    setStartDate(nextStart);
    setEndDate(nextEnd);
    setFilters((prev) => ({
      ...prev,
      check_in_date: '',
      check_in_from: nextStart ? nextStart.format('YYYY-MM-DD') : '',
      check_in_to: nextEnd ? nextEnd.format('YYYY-MM-DD') : '',
    }));
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleRefreshAndReset = () => {
    setFilters(createDefaultFilters());
    setStartDate(null);
    setEndDate(null);
    setSortConfig({ ...DEFAULT_SORT });
    setPagination((prev) => ({ ...prev, page: 0 }));
    setToolbarResetKey((k) => k + 1);
  };

  const openInvite = useCallback((row) => {
    const inviteId = Number(row?.id);
    if (!Number.isInteger(inviteId) || inviteId <= 0) return;
    setSelectedInviteId(inviteId);
    setDrawerOpen(true);
  }, []);

  const columns = [
    {
      field: 'room',
      label: 'Room',
      sortable: true,
      render: (_value, row) => <RoomCell row={row} />,
    },
    {
      field: 'guest_name',
      label: 'Guest',
      sortable: true,
      render: (value, row) => (
        <Link
          component="button"
          variant="body2"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openInvite(row);
          }}
          sx={{
            textDecoration: 'none',
            fontWeight: 600,
            color: 'primary.main',
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {value || '—'}
        </Link>
      ),
    },
    {
      field: 'guest_phone',
      label: 'Contact',
      sortable: false,
      render: (_value, row) => <ContactCell row={row} />,
    },
    {
      field: 'check_in_date',
      label: 'Stay',
      sortable: true,
      render: (_value, row) => <InviteStayCell row={row} />,
    },
    {
      field: 'expires_at',
      label: 'Link',
      sortable: true,
      render: (_value, row) => <LinkTimingCell row={row} />,
    },
    {
      field: 'payment_status',
      label: 'Payment',
      sortable: true,
      render: (_value, row) => (
        <Stack spacing={0.15} sx={{ minWidth: 0 }}>
          <Typography variant="body2" noWrap>
            {row.payment_requirement_display || row.payment_requirement || '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {row.payment_status_display || row.payment_status || '—'}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'status',
      label: 'Status',
      sortable: true,
      render: (value, row) => (
        <StatusChip status={value} label={row.status_display || value} />
      ),
    },
  ];

  const filterRowCells = columns.map((column) => {
    switch (column.field) {
      case 'room':
        return (
          <FormTextField
            key={column.field}
            size="small"
            label="Room"
            value={filters.room}
            onChange={(e) => handleFilterChange('room', e.target.value)}
            sx={{ minWidth: 100 }}
          />
        );
      case 'guest_name':
        return (
          <FormTextField
            key={column.field}
            size="small"
            label="Guest"
            value={filters.guest}
            onChange={(e) => handleFilterChange('guest', e.target.value)}
            sx={{ minWidth: 120 }}
          />
        );
      case 'guest_phone':
        return (
          <FormTextField
            key={column.field}
            size="small"
            label="Contact"
            value={filters.phone}
            onChange={(e) => handleFilterChange('phone', e.target.value)}
            sx={{ minWidth: 140 }}
          />
        );
      case 'check_in_date':
        return (
          <FormDatePicker
            key={column.field}
            label="Stay Date"
            value={filters.check_in_date ? moment(filters.check_in_date) : null}
            onChange={(newValue) =>
              handleFilterChange('check_in_date', newValue ? newValue.format('YYYY-MM-DD') : '')
            }
            sx={{ minWidth: 140 }}
          />
        );
      case 'expires_at':
        return <div key={column.field} />;
      case 'payment_status':
        return (
          <FormSelect
            key={column.field}
            label=""
            value={filters.payment_status}
            onChange={(e) => handleFilterChange('payment_status', e.target.value)}
            displayEmpty
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="">All payment</MenuItem>
            <MenuItem value="not_required">Not Required</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
          </FormSelect>
        );
      case 'status':
        return (
          <FormSelect
            key={column.field}
            label=""
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            displayEmpty
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="submitted">Submitted</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </FormSelect>
        );
      default:
        return <div key={column.field} />;
    }
  });

  return (
    <Box>
      {error && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
          {error}
        </Box>
      )}

      <DataTable
        columns={columns}
        data={rows}
        loading={loading}
        pagination={pagination}
        onPageChange={(page) => setPagination((prev) => ({ ...prev, page }))}
        onPerPageChange={(perPage) => setPagination((prev) => ({ ...prev, perPage, page: 0 }))}
        onSortChange={(nextSort) => {
          setSortConfig(nextSort);
          setPagination((prev) => ({ ...prev, page: 0 }));
        }}
        sortConfig={sortConfig}
        emptyMessage="No web check-in invites found matching the selected filters."
        stickyHeader
        filterRow={filterRowCells}
        onRowClick={openInvite}
        toolbarRight={
          <DashboardReportHeader
            key={toolbarResetKey}
            actionsOnly
            startDate={startDate}
            endDate={endDate}
            onDateRangeChange={handleDateRangeChange}
            onRefresh={handleRefreshAndReset}
            loading={loading}
            showPdf={false}
            themeMode={isDark ? 'dark' : 'light'}
            accentColor={ACCENT}
            allowAllPreset
            initialPreset="all"
          />
        }
      />

      <WebCheckInInviteDrawer
        open={drawerOpen}
        inviteId={selectedInviteId}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedInviteId(null);
        }}
        onUpdated={() => {
          fetchInvites();
        }}
      />
    </Box>
  );
};

export default WebCheckInList;
